/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useEffect } from "react";
import { API, CONFIGUREROUTES, DIALOGOPENFOR, LOCAL_STORAGE } from "../../../globals/globals";
import { lteX2connectionsColumns, ngX2connectionsColumns } from "./../../../components/Shared/Table/Columns";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { paginationModelSelector } from "../../../store/helpers/helperSelectors";
import { useGetNgX2ConnectionsQuery, useGetLteX2ConnectionsQuery } from "../../../store/api/deviceConfig/deviceConfigApiSlice";
import {
  setConfigCounts,
  setLteX2ConnectionList,
  setLteX2ConnectionIndexes,
  setNgX2ConnectionList,
  setNgX2ConnectionIndexes,
} from "../../../store/api/deviceConfig/reducers";
import { useGetConfigStatusQuery, useSetDeviceConfigMutation } from "../../../store/api/deviceConfig/deviceConfigApiSlice";
import { useStoreDispatch } from "../../../store/hooks";
import { OPERATIONTYPES, constStr, xsdPathStr } from "../../../globals/constants";
import { selectedLteX2ConnectionSelector, selectedNgX2ConnectionSelector } from "../../../store/api/deviceConfig/selectors";
import { IFullDeviceDetails, IltepeerInterfaces, IngpeerInterfaces } from "../../../store/api/deviceConfig/types";
import { confirmDialogSelector } from "../../../store/helpers/helperSelectors";
import { setOpenToast } from "../../../store/helpers/helperReducers";
import { prepareEnablePortPayload, prepareLteX2ConnectionPayload, prepareNgX2ConnectionPayload } from "../../../components/Configuration/helpers";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { errorMessage } from "../../../utils/helpers";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import ProfilePermissions from "../../../utils/ProfilePermissions";
import { initialState } from "../../../store/helpers/helperInitialState";

export const useNgX2ConnectionsData = () => {
  const location = useLocation();
  const dispatch = useStoreDispatch();
  const locationPath = location?.pathname.split("/");
  const { ngX2Connection = initialState.pagination } = useSelector(paginationModelSelector);
  const deviceDetail: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

  const args = { filters: `page=${ngX2Connection.page.toString()}&size=${ngX2Connection.pageSize.toString()}`, deviceKey: locationPath[2] };
  const { data, isLoading, refetch } = useGetNgX2ConnectionsQuery(args, {
    pollingInterval: API.ENDPOINTS.NGX2CONNECTIONS.pollingInterval || API.POLLING_INTERVAL,
  });

  const [setDeviceConfig, configStatus] = useSetDeviceConfigMutation();

  /*
  const uiseId = localStorage.getItem(LOCAL_STORAGE.USER);
  const args1 = {
    userId: !!uiseId ? uiseId : "",
    tableId: xsdPathStr.ngX2ConnectionPeer.tableId,
    deviceObjectKey: locationPath[2],
  };

  const configStatusData = useGetConfigStatusQuery(args1, {
    pollingInterval: API.ENDPOINTS.CONFIGSTATUS.pollingInterval,
  });
*/

  const selectedNgX2Connection = useSelector(selectedNgX2ConnectionSelector);
  const selectedDevice = useSelector(selectedDeviceSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);

  useEffect(() => {
    if (data?.totalItems != null && data?.totalItems >= 0) {
      dispatch(setConfigCounts({ [CONFIGUREROUTES.NGX2CONNECTION.Path]: data?.totalItems || 0 }));
    }
  }, [data?.totalItems, dispatch]);

  useEffect(() => {
    if (!!data?.data && data?.data[0].ngpeerInterfaces.length > 0) {
      dispatch(setNgX2ConnectionList(data?.data[0].ngpeerInterfaces!));
      dispatch(
        setNgX2ConnectionIndexes(
          data?.data[0].ngpeerInterfaces?.map(
            (item: IngpeerInterfaces) => Object.keys(item).map((keyitem) => keyitem === "index") && item.index
          ) as string[]
        )
      );
    } else {
      dispatch(setNgX2ConnectionList([]));
      dispatch(setNgX2ConnectionIndexes([]));
    }
  }, [data?.data, dispatch]);

  const deleteNgX2ConnectionHandler = async (actionName: string) => {
    if (!actionName) return;
    try {
      let response: any;
      let messageStr: string = "";
      if (actionName.match(constStr.yesDelete.toLowerCase())) {
        if (openConfirmDialog === DIALOGOPENFOR.NGX2CONNECTION) {
          const ngX2ConnectionPayload =
            !!selectedDevice?.objectKey &&
            prepareNgX2ConnectionPayload(undefined, selectedNgX2Connection, selectedDevice?.objectKey, OPERATIONTYPES.DELETE);
          response = await setDeviceConfig(ngX2ConnectionPayload);
          messageStr = `The deletion process for NG X2Connection Peer ${selectedNgX2Connection?.index} has been initiated.`;
        }

        if (response?.error) throw response.error;
        if (response?.data?.status === 200 || response?.data?.statusCodeValue === 200 || response?.data?.statusCode === "OK") {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: messageStr,
            })
          );
          refetch();
        }
      }
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  const handleUpdate = async (newObj: any, enableVal: any, localPort: any) => {
    try {
      let response: any;
      const operationType = OPERATIONTYPES.UPDATE;
      const ngX2ConnectionPayload = prepareEnablePortPayload(
        newObj,
        enableVal,
        localPort,
        deviceDetail?.objectKey,
        operationType,
        deviceDetail?.nodeType
      );
      response = await setDeviceConfig(ngX2ConnectionPayload);
      if (response?.error || configStatus?.isError) throw response?.error;
      if (response.data?.status === 200) {
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: `The data update request has submitted`,
          })
        );
      } else {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: `Data update request failed ${response.data} ` }));
        refetch();
      }
    } catch (error) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      refetch();
    }
    //handle prepareZonePayload for UPDATE
  };

  return {
    data,
    isLoading,
    permissions,
    handleUpdate,
    ngX2connectionsColumns,
    selectedNgX2Connection,
    deleteNgX2ConnectionHandler,
  };
};

export const useLteX2ConnectionsData = () => {
  const location = useLocation();
  const dispatch = useStoreDispatch();
  const locationPath = location?.pathname.split("/");
  const { lteX2Connection = initialState.pagination } = useSelector(paginationModelSelector);
  const deviceDetail: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

  const args = { filters: `page=${lteX2Connection.page.toString()}&size=${lteX2Connection.pageSize.toString()}`, deviceKey: locationPath[2] };
  const { data, isLoading, refetch } = useGetLteX2ConnectionsQuery(args, {
    pollingInterval: API.ENDPOINTS.LTEX2CONNECTIONS.pollingInterval || API.POLLING_INTERVAL,
  });

  const [setDeviceConfig, configStatus] = useSetDeviceConfigMutation();

  const uiseId = localStorage.getItem(LOCAL_STORAGE.USER);
  const args1 = {
    userId: !!uiseId ? uiseId : "",
    tableId: xsdPathStr.lteX2ConnectionPeer.tableId,
    deviceObjectKey: locationPath[2],
  };

  const configStatusData = useGetConfigStatusQuery(args1, {
    pollingInterval: API.ENDPOINTS.CONFIGSTATUS.pollingInterval,
  });

  const selectedLteX2Connection = useSelector(selectedLteX2ConnectionSelector);
  const selectedDevice = useSelector(selectedDeviceSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);

  useEffect(() => {
    if (data?.totalItems != null && data?.totalItems >= 0) {
      dispatch(setConfigCounts({ [CONFIGUREROUTES.LTEX2CONNECTION.Path]: data?.totalItems || 0 }));
    }
  }, [data?.totalItems, dispatch]);

  useEffect(() => {
    if (!!data?.data && data?.data[0].ltepeerInterfaces.length > 0) {
      dispatch(setLteX2ConnectionList(data?.data[0].ltepeerInterfaces!));
      dispatch(
        setLteX2ConnectionIndexes(
          data?.data[0].ltepeerInterfaces?.map(
            (item: IltepeerInterfaces) => Object.keys(item).map((keyitem) => keyitem === "index") && item.index
          ) as string[]
        )
      );
    } else {
      dispatch(setLteX2ConnectionList([]));
      dispatch(setLteX2ConnectionIndexes([]));
    }
  }, [data?.data, dispatch]);

  const deleteLteX2ConnectionHandler = async (actionName: string) => {
    if (!actionName) return;
    try {
      let response: any;
      let messageStr: string = "";
      if (actionName.match(constStr.yesDelete.toLowerCase())) {
        if (openConfirmDialog === DIALOGOPENFOR.LTEX2CONNECTION) {
          const LteX2ConnectionPayload =
            !!selectedDevice?.objectKey &&
            prepareLteX2ConnectionPayload(undefined, selectedLteX2Connection, selectedDevice?.objectKey, OPERATIONTYPES.DELETE);
          response = await setDeviceConfig(LteX2ConnectionPayload);
          messageStr = `The deletion process for LTE X2Connection Peer ${selectedLteX2Connection?.index} has been initiated.`;
        }

        if (response?.error) throw response.error;
        if (response?.data?.status === 200 || response?.data?.statusCodeValue === 200 || response?.data?.statusCode === "OK") {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: messageStr,
            })
          );
          refetch();
        }
      }
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  const handleUpdate = async (newObj: any, enableVal: any, localPort: any) => {
    try {
      let response: any;
      const operationType = OPERATIONTYPES.UPDATE;
      const lteX2ConnectionPayload = prepareEnablePortPayload(
        newObj,
        enableVal,
        localPort,
        deviceDetail?.objectKey,
        operationType,
        deviceDetail?.nodeType
      );
      response = await setDeviceConfig(lteX2ConnectionPayload);
      if (response?.error || configStatus?.isError) throw response?.error;
      if (response.data?.status === 200) {
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: `The data update request has submitted`,
          })
        );
        refetch();
      } else {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: `Data update request failed ${response.data} ` }));
        refetch();
      }
    } catch (error) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      refetch();
    }
    //handle prepareZonePayload for UPDATE
  };

  return {
    data,
    isLoading,
    permissions,
    lteX2connectionsColumns,
    selectedLteX2Connection,
    configStatusData,
    deleteLteX2ConnectionHandler,
    handleUpdate,
  };
};
